/**
 * @module buttonProps
 * @description collection of objects with info on button to be created and added to button group
 * @structure:
 *   type: string for button type (must match BamButtonGroup ALLOWED_KEYS: https://github.mlbam.net/fed-packages/bam-game-utils/blob/master/lib/BamButtonGroup.js#L4-L5)
 *   shouldUse: function (game) use game data to determine if button should be used based on data
 *   linkerFunction: name of function used in mlb-linker
 *   text: {
 *     dictionary: dictionary key for button text
 *     fallback: fallback text for button if dictionary key doesn't exist
 *   }
 *   glyph: name of glyph from https://qa-aws.mlb.com/styleguide#pattern-icon
 *   target: <href> target value
 */
export default {
    preview: {
        type: 'preview',
        linkerFunction: 'getPreviewLink',
        text: {
            dictionary: 'button_preview',
            fallback: 'Preview'
        },
        accessibility: {
            dictionary: 'button_preview',
            fallback: 'Preview'
        },
        glyph: 'recap',
        target: '_self'
    },
    deepLinkPreview: {
        type: 'preview',
        linkerFunction: 'getDeviceDeepLink',
        text: {
            dictionary: 'button_preview',
            fallback: 'Preview'
        },
        accessibility: {
            dictionary: 'button_preview',
            fallback: 'Preview'
        },
        glyph: 'recap',
        target: '_self'
    },
    watch: {
        type: 'watch',
        linkerFunction: 'getNewTvLink',
        text: {
            dictionary: 'button_mlbtv',
            fallback: 'MLB.TV'
        },
        accessibility: {
            dictionary: 'button_mlbtv',
            fallback: 'MLB.TV'
        },
        glyph: 'watch',
        target: '_self'
    },
    deepLinkWatch: {
        type: 'watch',
        linkerFunction: 'getDeviceTvDeepLink',
        text: {
            dictionary: 'button_mlbtv',
            fallback: 'MLB.TV'
        },
        accessibility: {
            dictionary: 'button_mlbtv',
            fallback: 'MLB.TV'
        },
        glyph: 'watch',
        target: '_self'
    },

    watchMilb: {
        type: 'watch',
        linkerFunction: 'getTvLink',
        text: {
            dictionary: 'button_milbtv',
            fallback: 'MiLB.TV'
        },
        accessibility: {
            dictionary: 'button_milbtv_accessibility',
            fallback: 'Em Eye Elle Bee dot Tee Vee'
        },
        glyph: 'watch',
        target: '_self'
    },
    watchSubscribe: {
        type: 'watch',
        linkerFunction: 'getTvLinkOrTvSubscriptionLink',
        text: {
            dictionary: 'button_mlbtv',
            fallback: 'MLB.TV'
        },
        accessibility: {
            dictionary: 'button_mlbtv',
            fallback: 'MLB.TV'
        },
        glyph: 'watch',
        target: '_self'
    },
    watchMilbSubscribe: {
        type: 'watch',
        linkerFunction: 'getTvLinkOrTvSubscriptionLink',
        text: {
            dictionary: 'button_milbtv',
            fallback: 'Em Eye Elle Bee'
        },
        accessibility: {
            dictionary: 'button_milbtv_accessibility',
            fallback: 'Em Eye Elle Bee'
        },
        glyph: 'watch',
        target: '_self'
    },
    gameday: {
        type: 'gameday',
        linkerFunction: 'getGamedayLink',
        text: {
            dictionary: 'button_gameday',
            fallback: 'Gameday'
        },
        accessibility: {
            dictionary: 'button_gameday',
            fallback: 'Gameday'
        },
        glyph: 'gameday',
        target: '_self'
    },
    deepLinkGameday: {
        type: 'gameday',
        linkerFunction: 'getDeviceDeepLink',
        text: {
            dictionary: 'button_gameday',
            fallback: 'Gameday'
        },
        accessibility: {
            dictionary: 'button_gameday',
            fallback: 'Gameday'
        },
        glyph: 'gameday',
        target: '_self'
    },
    tickets: {
        type: 'tickets',
        linkerFunction: 'getTicketsLink',
        text: {
            dictionary: 'button_tickets',
            fallback: 'Tickets'
        },
        accessibility: {
            dictionary: 'tickets_buy',
            fallback: 'Buy Tickets'
        },
        glyph: 'tickets',
        target: '_blank'
    },
    audio: {
        type: 'audio',
        linkerFunction: 'getAudioLink',
        text: {
            dictionary: 'button_audio',
            fallback: 'Audio'
        },
        accessibility: {
            dictionary: 'button_audio',
            fallback: 'Audio'
        },
        glyph: 'audio',
        target: '_self'
    },
    audioMilbTeam: {
        type: 'audio',
        linkerFunction: 'getTuneInLinkForContextTeam',
        text: {
            dictionary: 'button_audio',
            fallback: 'Audio'
        },
        accessibility: {
            dictionary: 'button_audio',
            fallback: 'Audio'
        },
        glyph: 'audio',
        target: '_self'
    },
    audioMilb: {
        type: 'audio',
        linkerFunction: 'getTuneInLink',
        text: {
            dictionary: 'button_audio',
            fallback: 'Audio'
        },
        accessibility: {
            dictionary: 'button_audio',
            fallback: 'Audio'
        },
        glyph: 'audio',
        target: '_self'
    },
    boxscore: {
        type: 'boxscore',
        linkerFunction: 'getBoxscoreLink',
        text: {
            dictionary: 'button_box',
            fallback: 'Box'
        },
        accessibility: {
            dictionary: 'button_box',
            fallback: 'Box'
        },
        glyph: 'line-score',
        target: '_self'
    },
    vidhighlights: {
        type: 'vidhighlights',
        linkerFunction: 'getVideoHighlightsLink',
        text: {
            dictionary: 'button_video',
            fallback: 'Video'
        },
        accessibility: {
            dictionary: 'button_video_watch_highlight',
            fallback: 'Watch highlights'
        },
        glyph: 'video',
        target: '_self'
    },
    //TODO: Remove recap link in favor of wrap
    recap: {
        type: 'recap',
        linkerFunction: 'getWrapLink',
        text: {
            dictionary: 'button_wrap',
            fallback: 'Wrap'
        },
        accessibility: {
            dictionary: 'button_wrap',
            fallback: 'Wrap'
        },
        glyph: 'recap',
        target: '_self'
    },
    wrap: {
        type: 'wrap',
        linkerFunction: 'getWrapLink',
        text: {
            dictionary: 'button_wrap',
            fallback: 'Wrap'
        },
        accessibility: {
            dictionary: 'button_wrap',
            fallback: 'Wrap'
        },
        glyph: 'recap',
        target: '_self'
    },
    deepLinkWrap: {
        type: 'wrap',
        linkerFunction: 'getDeviceDeepLink',
        text: {
            dictionary: 'button_wrap',
            fallback: 'Wrap'
        },
        accessibility: {
            dictionary: 'button_wrap',
            fallback: 'Wrap'
        },
        glyph: 'recap',
        target: '_self'
    },
    youtubefgow: {
        type: 'youtubefgow',
        linkerFunction: 'getYouTubeGameLink',
        text: {
            dictionary: 'youtube_abbrv_label',
            fallback: 'YouTube'
        },
        accessibility: {
            dictionary: 'youtube_label',
            fallback: 'Live on YouTube - Watch Free'
        },
        glyph: 'youtube',
        target: '_self'
    },
    fgow: {
        type: 'fgow',
        linkerFunction: 'getFacebookGameLink',
        text: {
            dictionary: 'button_fgow',
            fallback: 'FB Watch'
        },
        accessibility: {
            dictionary: 'button_fgow',
            fallback: 'FB Watch'
        },
        glyph: 'facebook-watch',
        target: '_self'
    },
    scheduleMilb: {
        type: 'schedule',
        linkerFunction: 'getScheduleLink',
        text: {
            dictionary: 'schedule_label',
            fallback: 'Schedule'
        },
        accessibility: {
            dictionary: 'schedule_label',
            fallback: 'Schedule'
        },
        glyph: 'calendar',
        target: '_self'
    },
    liveBox: {
        type: 'liveBox',
        linkerFunction: 'getLiveBoxLink',
        text: {
            dictionary: 'button_box',
            fallback: 'Box'
        },
        accessibility: {
            dictionary: 'button_box',
            fallback: 'Box'
        },
        glyph: 'line-score',
        target: '_self'
    }
};
